import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import {
  useGetMembershipListQuery,
  useGetMembershipsQuery,
} from "@/services/membershipsApi";
import { useGetRefundValuesQuery } from "@/services/systemConfigApi";
import { useGetGroupQuery } from "@/services/groupsApis";
import { useGetMemberGroupsQuery } from "@/services/memberGroupApis";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { toast } from "@/components/ui/use-toast";
import { useState, useEffect, useMemo } from "react";
import {
  Users,
  Calendar,
  Layers,
  Plus,
  Minus,
  Trash2,
  Shield,
  AlertCircle,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { FacilitySchedule } from "@/types/facility/schedule";
import Combobox from "@/components/ui/common/combobox";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface EventPricingAndRefundProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  open: boolean;
}

const EventPricingAndRefund = ({
  control,
  errors,
  watch,
  setValue,
  open,
}: EventPricingAndRefundProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  // Fetch membership plans for mock data since APIs aren't ready
  const { data: membershipPlans, isLoading: membershipLoading } =
    useGetMembershipsQuery(
      {
        query: `sort_key=id&sort_order=desc&status=active`,
        org_id: orgId,
      },
      {
        skip: !orgId && !open,
      },
    );

  // Fetch groups for group pricing
  const { data: membershipGroupData, isLoading: membershipGroupLoading } =
    useGetGroupQuery(orgId, {
      skip: !orgId && !open,
    });

  const { data: memberGroupData, isLoading: memberGroupLoading } =
    useGetMemberGroupsQuery(orgId, {
      skip: !orgId && !open,
    });

  // Mock refund values since APIs aren't ready
  // const { data: refundValues } = useGetRefundValuesQuery();

  // State for toggles
  const [planBasedPricingEnabled, setPlanBasedPricingEnabled] = useState(false);
  const [groupBasedPricingEnabled, setGroupBasedPricingEnabled] =
    useState(false);
  const [specialPricingEnabled, setSpecialPricingEnabled] = useState(false);
  const [refundsEnabled, setRefundsEnabled] = useState(false);

  // Watch arrays to sync with toggle states
  const planPricing = watch("plan_pricing") || [];
  const groupPricing = watch("group_pricing") || [];
  const specialPricing = watch("special_pricing") || [];
  const refundPolicies = watch("refund_policies") || [];

  // Watch form values for reactive date calculations
  const scheduleStartDate = watch("general_info.start_date");
  const scheduleEndDate = watch("general_info.end_date");
  const scheduleId = watch("general_info.id");
  const formData = watch();
  const regularPrice = watch("general_info.price") || 0;

  // Initialize & sync toggle states based on current form data (including after resets)
  useEffect(() => {
    setPlanBasedPricingEnabled((planPricing || []).length > 0);
    setGroupBasedPricingEnabled((groupPricing || []).length > 0);
    setSpecialPricingEnabled((specialPricing || []).length > 0);
    setRefundsEnabled((refundPolicies || []).length > 0);
  }, [planPricing, groupPricing, specialPricing, refundPolicies]);

  const membershipPlanOptions = useMemo(() => {
    return membershipPlans?.data.map((plan) => ({
      label: plan.name,
      value: String(plan.id),
      group_id: plan.group_id,
    }));
  }, [membershipPlans]);

  const membershipGroupOptions = useMemo(() => {
    return Array.isArray(membershipGroupData)
      ? membershipGroupData.map((group) => ({
          label: group.label,
          value: String(group.value),
        }))
      : [];
  }, [membershipGroupData]);

  const memberGroupOptions = useMemo(() => {
    return memberGroupData && Array.isArray(memberGroupData.data)
      ? memberGroupData.data.map((group) => ({
          label: group.name,
          value: String(group.id),
        }))
      : [];
  }, [memberGroupData]);

  return (
    <div className="space-y-6">
      {/* Pricing Section */}
      <Card className="p-6 shadow-sm border-l-4 border-l-blue-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <span className="h-5 w-5 text-blue-600">Rs.</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Pricing Configuration
            </h2>
            <p className="text-sm text-gray-600">
              Set up pricing options for your facility bookings
            </p>
          </div>
        </div>
        <Separator className="mb-6" />

        {/* Regular Pricing */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              Required
            </Badge>
            <h3 className="font-medium text-gray-900">Regular Pricing</h3>
          </div>
          <Controller
            name="general_info.price"
            control={control}
            rules={{
              required: "Regular pricing is required",
              min: { value: 0, message: "Price must be positive" },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className="space-y-2">
                <div className="relative">
                  <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500">
                    Rs.
                  </p>
                  <Input
                    className="pl-8 max-w-xs"
                    type="number"
                    min="0"
                    step="1"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    placeholder="0.00"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-1 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {error.message}
                  </div>
                )}
                <p className="text-xs text-gray-600">
                  This price will be populated from the facility settings but
                  can be overridden here.
                </p>
              </div>
            )}
          />
        </div>

        {/* Plan Based Pricing Section */}
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  Plan Based Pricing
                </h3>
                <p className="text-xs text-gray-600">
                  Set different prices for different membership plans
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Enable</span>
              <Switch
                checked={planBasedPricingEnabled}
                onCheckedChange={(checked) => {
                  setPlanBasedPricingEnabled(checked);
                  if (checked) {
                    setValue("plan_pricing", [
                      { group_id: 0, membership_ids: [], price: 0 },
                    ]);
                  } else {
                    setValue("plan_pricing", []);
                  }
                }}
              />
            </div>
          </div>

          {planBasedPricingEnabled && (
            <div className="space-y-4 mt-4">
              <Controller
                control={control}
                name="plan_pricing"
                rules={{
                  validate: {
                    plansValid: (plans) => {
                      if (!plans || plans.length === 0) return true;
                      if (plans.length > 7) return "Maximum 7 plans allowed";
                      if (regularPrice <= 0)
                        return "Please set a regular schedule price before adding plan-based pricing.";
                      for (let i = 0; i < plans.length; i++) {
                        if (!plans[i].group_id) {
                          return `Plan ${i + 1}: Membership Group is required`;
                        }
                        if (
                          !plans[i].membership_ids ||
                          plans[i].membership_ids.length === 0
                        ) {
                          return `Plan ${i + 1}: At least one membership plan is required`;
                        }
                        if (plans[i].price <= 0) {
                          return `Plan ${i + 1}: Price should be greater than zero`;
                        }
                        // if (plans[i].price >= regularPrice) {
                        //   return `Plan ${i + 1}: Price must be lower than the regular schedule price (Rs. ${regularPrice}).`;
                        // }
                      }
                      // Check for duplicate groups if needed, or duplicate memberships across plans?
                      // User requirement didn't specify strict uniqueness per group, but usually one group entry is enough if multiselect allowed?
                      // Actually, if I select different memberships in different lines for same group, valid?
                      // Ideally we should check if any membership is selected in multiple lines.
                      const allMembershipIds = plans.flatMap(
                        (p) => p.membership_ids,
                      );
                      const uniqueMembershipIds = new Set(allMembershipIds);
                      if (
                        allMembershipIds.length !== uniqueMembershipIds.size
                      ) {
                        return "Duplicate membership plans selected across different entries";
                      }

                      return true;
                    },
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-4">
                    {error && (
                      <p className="text-red-500 text-sm">{error.message}</p>
                    )}
                    {field.value?.map((plan, index) => (
                      <Card
                        key={index}
                        className="p-4 bg-white border-2 border-green-100 hover:border-green-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs text-green-700 border-green-300"
                            >
                              Plan {index + 1}
                            </Badge>
                          </div>
                          {(field.value?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newPlans = [...(field.value || [])];
                                newPlans.splice(index, 1);
                                field.onChange(newPlans);
                              }}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Membership Group{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <Combobox
                              list={membershipGroupOptions}
                              defaultValue={
                                membershipGroupOptions?.find(
                                  (g) => g.value === String(plan.group_id),
                                )?.label || ""
                              }
                              setFilter={(selectedLabel: string) => {
                                const selectedOption =
                                  membershipGroupOptions?.find(
                                    (option) => option.label === selectedLabel,
                                  );
                                const newPlans = [...(field.value || [])];
                                if (selectedOption) {
                                  if (
                                    newPlans[index].group_id !==
                                    Number(selectedOption.value)
                                  ) {
                                    newPlans[index].group_id = Number(
                                      selectedOption.value,
                                    );
                                    newPlans[index].membership_ids = []; // Reset memberships when group changes
                                  }
                                } else {
                                  newPlans[index].group_id = 0;
                                  newPlans[index].membership_ids = [];
                                }
                                field.onChange(newPlans);
                              }}
                              label="Membership Group"
                              showLabel={false}
                              disabled={membershipGroupLoading}
                              className={
                                errors.plan_pricing?.[index]?.group_id
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {errors.plan_pricing?.[index]?.group_id && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.plan_pricing?.[index]?.group_id
                                    ?.message
                                }
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Membership Plan{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <MultiSelect
                              options={
                                membershipPlanOptions
                                  ?.filter((m) => m.group_id === plan.group_id)
                                  .map((m) => ({
                                    label: m.label,
                                    value: Number(m.value),
                                  })) || []
                              }
                              value={plan.membership_ids}
                              onChange={(selected: (string | number)[]) => {
                                const newPlans = [...(field.value || [])];
                                newPlans[index].membership_ids =
                                  selected.map(Number);
                                field.onChange(newPlans);
                              }}
                              placeholder="Select Plans"
                              disabled={!plan.group_id}
                            />

                            {errors.plan_pricing?.[index]?.membership_ids && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.plan_pricing?.[index]?.membership_ids
                                    ?.message
                                }
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500">
                                Rs.
                              </p>

                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={plan.price}
                                className={`pl-10 ${errors.plan_pricing?.[index]?.price ? "border-red-500" : ""}`}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  const newPlans = [...(field.value || [])];
                                  if (value <= 0) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Price must be greater than zero",
                                    });
                                    newPlans[index].price = 0.01;
                                  } else {
                                    newPlans[index].price = value;
                                  }
                                  field.onChange(newPlans);
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            {errors.plan_pricing?.[index]?.price && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors.plan_pricing?.[index]?.price?.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}

                    <div className="flex items-center justify-between pt-4 border-t border-green-200">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                          onClick={() => {
                            if ((field.value?.length || 0) < 7) {
                              field.onChange([
                                ...(field.value || []),
                                { group_id: 0, membership_ids: [], price: 0 },
                              ]);
                            } else {
                              toast({
                                variant: "destructive",
                                description: "Maximum 7 plans allowed",
                              });
                            }
                          }}
                          disabled={
                            membershipLoading || (field.value?.length || 0) >= 7
                          }
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Plan
                        </Button>
                        <div className="text-xs text-gray-500">
                          {field.value?.length || 0}/7 rules
                        </div>
                      </div>

                      {(field.value?.length || 0) > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-gray-500 hover:text-red-500"
                          onClick={() => {
                            const newPlans = [...(field.value || [])];
                            newPlans.pop();
                            field.onChange(newPlans);
                          }}
                        >
                          <Minus className="h-4 w-4 mr-2" />
                          Remove Last
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </div>

        {/* Group Based Pricing Section */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Layers className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  Group Based Pricing
                </h3>
                <p className="text-xs text-gray-600">
                  Set different prices for specific member or membership groups
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Enable</span>
              <Switch
                checked={groupBasedPricingEnabled}
                onCheckedChange={(checked) => {
                  setGroupBasedPricingEnabled(checked);
                  if (checked) {
                    setValue("group_pricing", [{ group_id: 0, price: 0 }]);
                  } else {
                    setValue("group_pricing", []);
                  }
                }}
              />
            </div>
          </div>

          {groupBasedPricingEnabled && (
            <div className="space-y-4 mt-4">
              <Controller
                control={control}
                name="group_pricing"
                rules={{
                  validate: {
                    groupsValid: (groups) => {
                      if (!groups || groups.length === 0) return true;
                      if (groups.length > 7)
                        return "Maximum 7 group rules allowed";
                      if (regularPrice <= 0)
                        return "Please set a regular schedule price before adding group-based pricing.";
                      for (let i = 0; i < groups.length; i++) {
                        if (!groups[i].group_id) {
                          return `Group Rule ${i + 1}: Member Group is required`;
                        }
                        if (groups[i].price <= 0) {
                          return `Group Rule ${i + 1}: Price should be greater than zero`;
                        }
                        // if (groups[i].price >= regularPrice) {
                        //   return `Group Rule ${i + 1}: Price must be lower than the regular schedule price (Rs. ${regularPrice}).`;
                        // }
                      }
                      const groupIds = groups.map((g) => g.group_id);
                      const uniqueGroupIds = new Set(
                        groupIds.filter((id) => id !== 0),
                      );
                      if (
                        groupIds.filter((id) => id !== 0).length !==
                        uniqueGroupIds.size
                      ) {
                        return "Duplicate groups are not allowed";
                      }
                      return true;
                    },
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-4">
                    {error && (
                      <p className="text-red-500 text-sm">{error.message}</p>
                    )}
                    {field.value?.map((rule, index) => (
                      <Card
                        key={index}
                        className="p-4 bg-white border-2 border-blue-100 hover:border-blue-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs text-blue-700 border-blue-300"
                            >
                              Group Rule {index + 1}
                            </Badge>
                          </div>
                          {(field.value?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newRules = [...(field.value || [])];
                                newRules.splice(index, 1);
                                field.onChange(newRules);
                              }}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Member Group{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <Combobox
                              list={memberGroupOptions}
                              defaultValue={
                                memberGroupOptions?.find(
                                  (g) => g.value === String(rule.group_id),
                                )?.label || ""
                              }
                              setFilter={(selectedLabel: string) => {
                                const selectedOption = memberGroupOptions?.find(
                                  (option) => option.label === selectedLabel,
                                );
                                const newRules = [...(field.value || [])];
                                if (selectedOption) {
                                  // Check for duplicate
                                  const isDuplicate = newRules.some(
                                    (r, i) =>
                                      i !== index &&
                                      r.group_id ===
                                        Number(selectedOption.value),
                                  );
                                  if (isDuplicate) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "This group is already selected. Please choose a different group.",
                                    });
                                    return;
                                  }

                                  newRules[index].group_id = Number(
                                    selectedOption.value,
                                  );
                                } else {
                                  newRules[index].group_id = 0;
                                }
                                field.onChange(newRules);
                              }}
                              label="Member Group"
                              showLabel={false}
                              disabled={memberGroupLoading}
                              className={
                                errors.group_pricing?.[index]?.group_id
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {errors.group_pricing?.[index]?.group_id && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.group_pricing?.[index]?.group_id
                                    ?.message
                                }
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500">
                                Rs.
                              </p>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={rule.price}
                                className={`pl-10 ${errors.group_pricing?.[index]?.price ? "border-red-500" : ""}`}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  const newRules = [...(field.value || [])];
                                  if (value <= 0) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Price must be greater than zero",
                                    });
                                    newRules[index].price = 0.01;
                                  } else {
                                    newRules[index].price = value;
                                  }
                                  field.onChange(newRules);
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            {errors.group_pricing?.[index]?.price && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors.group_pricing?.[index]?.price?.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}

                    <div className="flex items-center justify-between pt-4 border-t border-blue-200">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400"
                          onClick={() => {
                            if ((field.value?.length || 0) < 7) {
                              field.onChange([
                                ...(field.value || []),
                                { group_id: 0, price: 0 },
                              ]);
                            } else {
                              toast({
                                variant: "destructive",
                                description: "Maximum 7 group rules allowed",
                              });
                            }
                          }}
                          disabled={
                            memberGroupLoading ||
                            (field.value?.length || 0) >= 7
                          }
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Group Rule
                        </Button>
                        <div className="text-xs text-gray-500">
                          {field.value?.length || 0}/7 rules
                        </div>
                      </div>

                      {(field.value?.length || 0) > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-gray-500 hover:text-red-500"
                          onClick={() => {
                            const newRules = [...(field.value || [])];
                            newRules.pop();
                            field.onChange(newRules);
                          }}
                        >
                          <Minus className="h-4 w-4 mr-2" />
                          Remove Last
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </div>

        {/* Special Pricing Section */}
        <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Special Pricing</h3>
                <p className="text-xs text-gray-600">
                  Create time-based promotional pricing
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Enable</span>
              <Switch
                checked={specialPricingEnabled}
                onCheckedChange={(checked) => {
                  setSpecialPricingEnabled(checked);
                  if (checked) {
                    setValue("special_pricing", [
                      {
                        name: "",
                        price: 0,
                        date_from: "",
                        date_to: "",
                      },
                    ]);
                  } else {
                    setValue("special_pricing", []);
                  }
                }}
              />
            </div>
          </div>

          {specialPricingEnabled && (
            <div className="space-y-4 mt-4">
              <Controller
                control={control}
                name="special_pricing"
                rules={{
                  validate: {
                    specialPricingValid: (rules) => {
                      if (!rules || rules.length === 0) return true;
                      const startDate = watch("general_info.start_date");
                      const endDate = watch("general_info.end_date");

                      for (let i = 0; i < rules.length; i++) {
                        if (!rules[i].name || rules[i].name.trim() === "") {
                          return `Rule ${i + 1}: Name is required`;
                        }
                        if (!rules[i].price || rules[i].price <= 0) {
                          return `Rule ${i + 1}: Price must be greater than zero`;
                        }
                        if (!rules[i].date_from) {
                          return `Rule ${i + 1}: Start date is required`;
                        }
                        if (!rules[i].date_to) {
                          return `Rule ${i + 1}: End date is required`;
                        }

                        const ruleStartDate = new Date(
                          rules[i].date_from as string,
                        );
                        const ruleEndDate = new Date(
                          rules[i].date_to as string,
                        );

                        if (startDate && ruleStartDate < new Date(startDate)) {
                          return `Rule ${i + 1}: Start date cannot be before event start date`;
                        }
                        if (endDate && ruleEndDate > new Date(endDate)) {
                          return `Rule ${i + 1}: End date cannot be after event end date`;
                        }
                        if (ruleStartDate >= ruleEndDate) {
                          return `Rule ${i + 1}: End date must be after start date`;
                        }

                        // Check for overlapping date ranges with other rules
                        for (let j = 0; j < rules.length; j++) {
                          if (
                            i !== j &&
                            rules[j].date_from &&
                            rules[j].date_to
                          ) {
                            const otherStartDate = new Date(
                              rules[j].date_from as string,
                            );
                            const otherEndDate = new Date(
                              rules[j].date_to as string,
                            );

                            // Check if the current rule overlaps with another rule
                            // Two date ranges overlap if: start1 < end2 AND start2 < end1
                            if (
                              ruleStartDate < otherEndDate &&
                              otherStartDate < ruleEndDate
                            ) {
                              return `Rule ${i + 1}: Date range overlaps with Rule ${j + 1}. Please ensure all special pricing date ranges are non-overlapping.`;
                            }
                          }
                        }
                      }
                      return true;
                    },
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-4">
                    {error && (
                      <p className="text-red-500 text-sm">{error.message}</p>
                    )}
                    {field.value?.map((rule, index) => (
                      <Card
                        key={index}
                        className="p-4 bg-white border-2 border-amber-100 hover:border-amber-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs text-amber-700 border-amber-300"
                            >
                              Rule {index + 1}
                            </Badge>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newRules = [...(field.value || [])];
                              newRules.splice(index, 1);
                              field.onChange(newRules);
                            }}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Rule Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                              value={rule.name}
                              className={
                                errors.special_pricing?.[index]?.name
                                  ? "border-red-500"
                                  : ""
                              }
                              onChange={(e) => {
                                const newRules = [...(field.value || [])];
                                newRules[index].name = e.target.value;
                                field.onChange(newRules);
                              }}
                              placeholder="E.g., Early Bird Special"
                            />
                            {errors.special_pricing?.[index]?.name && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors.special_pricing?.[index]?.name?.message}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500">
                                Rs.
                              </p>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={rule.price}
                                className={`pl-10 ${errors.special_pricing?.[index]?.price ? "border-red-500" : ""}`}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  const newRules = [...(field.value || [])];
                                  if (value <= 0) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Price must be greater than zero",
                                    });
                                    newRules[index].price = 0.01;
                                  } else {
                                    newRules[index].price = value;
                                  }
                                  field.onChange(newRules);
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            {errors.special_pricing?.[index]?.price && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.special_pricing?.[index]?.price
                                    ?.message
                                }
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              From Date <span className="text-red-500">*</span>
                            </label>
                            <Input
                              type="date"
                              required
                              className={
                                errors.special_pricing?.[index]?.date_from
                                  ? "border-red-500"
                                  : ""
                              }
                              value={rule.date_from || ""}
                              min={
                                watch("general_info.start_date") || undefined
                              }
                              max={watch("general_info.end_date") || undefined}
                              onChange={(e) => {
                                const newRules = [...(field.value || [])];
                                newRules[index].date_from = e.target.value;

                                if (
                                  e.target.value &&
                                  newRules[index].date_to &&
                                  new Date(e.target.value) >=
                                    new Date(newRules[index].date_to)
                                ) {
                                  newRules[index].date_to = "";
                                  toast({
                                    variant: "destructive",
                                    description:
                                      "End date must be after start date",
                                  });
                                }

                                // Check for overlaps with other rules
                                if (e.target.value && newRules[index].date_to) {
                                  const currentStart = new Date(e.target.value);
                                  const currentEnd = new Date(
                                    newRules[index].date_to,
                                  );

                                  for (let j = 0; j < newRules.length; j++) {
                                    if (
                                      j !== index &&
                                      newRules[j].date_from &&
                                      newRules[j].date_to
                                    ) {
                                      const otherStart = new Date(
                                        newRules[j].date_from,
                                      );
                                      const otherEnd = new Date(
                                        newRules[j].date_to,
                                      );

                                      if (
                                        currentStart < otherEnd &&
                                        otherStart < currentEnd
                                      ) {
                                        toast({
                                          variant: "destructive",
                                          description: `Date range overlaps with Rule ${j + 1}. Please ensure all special pricing date ranges are non-overlapping.`,
                                        });
                                        break;
                                      }
                                    }
                                  }
                                }

                                field.onChange(newRules);
                              }}
                            />
                            {errors.special_pricing?.[index]?.date_from && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.special_pricing?.[index]?.date_from
                                    ?.message
                                }
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              To Date <span className="text-red-500">*</span>
                            </label>
                            <Input
                              type="date"
                              required
                              className={
                                errors.special_pricing?.[index]?.date_to
                                  ? "border-red-500"
                                  : ""
                              }
                              value={rule.date_to || ""}
                              min={
                                rule.date_from ||
                                watch("general_info.start_date") ||
                                undefined
                              }
                              max={watch("general_info.end_date") || undefined}
                              onChange={(e) => {
                                const newRules = [...(field.value || [])];
                                newRules[index].date_to = e.target.value;

                                // Check for overlaps with other rules
                                if (
                                  e.target.value &&
                                  newRules[index].date_from
                                ) {
                                  const currentStart = new Date(
                                    newRules[index].date_from,
                                  );
                                  const currentEnd = new Date(e.target.value);

                                  for (let j = 0; j < newRules.length; j++) {
                                    if (
                                      j !== index &&
                                      newRules[j].date_from &&
                                      newRules[j].date_to
                                    ) {
                                      const otherStart = new Date(
                                        newRules[j].date_from,
                                      );
                                      const otherEnd = new Date(
                                        newRules[j].date_to,
                                      );

                                      if (
                                        currentStart < otherEnd &&
                                        otherStart < currentEnd
                                      ) {
                                        toast({
                                          variant: "destructive",
                                          description: `Date range overlaps with Rule ${j + 1}. Please ensure all special pricing date ranges are non-overlapping.`,
                                        });
                                        break;
                                      }
                                    }
                                  }
                                }

                                field.onChange(newRules);
                              }}
                            />
                            {errors.special_pricing?.[index]?.date_to && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.special_pricing?.[index]?.date_to
                                    ?.message
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}

                    <div className="pt-4 border-t border-amber-200">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
                        onClick={() => {
                          field.onChange([
                            ...(field.value || []),
                            {
                              name: "",
                              price: 0,
                              date_from: "",
                              date_to: "",
                            },
                          ]);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Special Pricing Rule
                      </Button>
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Refund policy section */}
      <Card className="p-6 shadow-sm border-l-4 border-l-red-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <Shield className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Refund Policy
            </h2>
            <p className="text-sm text-gray-600">
              Configure cancellation periods and refund percentages
            </p>
          </div>
        </div>
        <Separator className="mb-6" />

        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  Event Refund Rules
                </h3>
                <p className="text-xs text-gray-600">
                  Define cancellation periods and refund amounts
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Enable</span>
              <Switch
                checked={refundsEnabled}
                onCheckedChange={(checked) => {
                  setRefundsEnabled(checked);
                  if (checked) {
                    setValue("refund_policies", [
                      { cancellation_period: "", refund_percentage: 0 },
                    ]);
                  } else {
                    setValue("refund_policies", []);
                  }
                }}
              />
            </div>
          </div>

          {!refundsEnabled && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-700 text-sm">
                <Info className="h-4 w-4" />
                <span>
                  When disabled, no refunds will be available for this facility
                  schedule.
                </span>
              </div>
            </div>
          )}
        </div>

        {refundsEnabled && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 text-blue-700 text-sm">
                <Info className="h-4 w-4" />
                <span>
                  Set up to 7 refund rules. Each rule defines a cancellation
                  period and the corresponding refund percentage.
                </span>
              </div>
            </div>

            <Controller
              control={control}
              name="refund_policies"
              rules={{
                validate: {
                  refundPoliciesValid: (policies) => {
                    if (!policies || policies.length === 0)
                      return "At least one refund policy is required";
                    if (policies.length > 7)
                      return "Maximum 7 refund policies allowed";

                    const periods = new Set();
                    for (let i = 0; i < policies.length; i++) {
                      if (
                        !policies[i].cancellation_period ||
                        policies[i].cancellation_period.trim() === ""
                      ) {
                        return `Rule ${i + 1}: Cancellation period is required`;
                      }
                      if (periods.has(policies[i].cancellation_period)) {
                        return `Rule ${i + 1}: Duplicate cancellation period`;
                      }
                      periods.add(policies[i].cancellation_period);
                      if (
                        policies[i].refund_percentage <= 0 ||
                        policies[i].refund_percentage > 100
                      ) {
                        return `Rule ${i + 1}: Percentage must be between 0-100`;
                      }
                    }
                    return true;
                  },
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-center gap-2 text-red-700 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error.message}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {field.value?.map((rule, index) => (
                      <Card
                        key={index}
                        className="p-4 bg-white border-2 border-red-100 hover:border-red-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs text-red-700 border-red-300"
                            >
                              Rule {index + 1}
                            </Badge>
                          </div>
                          {(field.value?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newRefunds = [...(field.value || [])];
                                newRefunds.splice(index, 1);
                                field.onChange(newRefunds);
                              }}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Cancellation Period{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <Select
                              value={rule.cancellation_period || ""}
                              onValueChange={(value) => {
                                const isDuplicate =
                                  field.value?.some(
                                    (r, i) =>
                                      i !== index &&
                                      r.cancellation_period === value,
                                  ) || false;

                                if (isDuplicate) {
                                  toast({
                                    variant: "destructive",
                                    description:
                                      "This cancellation period is already selected. Please choose a different period.",
                                  });
                                  return;
                                }

                                const newRefunds = [...(field.value || [])];
                                newRefunds[index].cancellation_period = value;
                                field.onChange(newRefunds);
                              }}
                            >
                              <SelectTrigger
                                className={
                                  errors.refund_policies?.[index]
                                    ?.cancellation_period
                                    ? "border-red-500"
                                    : ""
                                }
                              >
                                <SelectValue placeholder="Select cancellation period" />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  {
                                    value: "1h",
                                    label: "1 hour before the booking starts",
                                  },
                                  {
                                    value: "2h",
                                    label: "2 hours before the booking starts",
                                  },
                                  {
                                    value: "4h",
                                    label: "4 hours before the booking starts",
                                  },
                                  {
                                    value: "6h",
                                    label: "6 hours before the booking starts",
                                  },
                                  {
                                    value: "12h",
                                    label: "12 hours before the booking starts",
                                  },
                                  {
                                    value: "24h",
                                    label: "24 hours before the booking starts",
                                  },
                                  {
                                    value: "2d",
                                    label: "2 days before the booking starts",
                                  },
                                  {
                                    value: "3d",
                                    label: "3 days before the booking starts",
                                  },
                                ].map((period) => (
                                  <SelectItem
                                    key={period.value}
                                    value={period.label}
                                  >
                                    {period.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.refund_policies?.[index]
                              ?.cancellation_period && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.refund_policies?.[index]
                                    ?.cancellation_period?.message
                                }
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Refund Percentage{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                step="1"
                                value={rule.refund_percentage || ""}
                                className={
                                  errors.refund_policies?.[index]
                                    ?.refund_percentage
                                    ? "border-red-500"
                                    : ""
                                }
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  const newRefunds = [...(field.value || [])];

                                  if (isNaN(value) || value <= 0) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Percentage must be between 0-100",
                                    });
                                    newRefunds[index].refund_percentage = 1;
                                  } else if (value > 100) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Percentage cannot exceed 100%",
                                    });
                                    newRefunds[index].refund_percentage = 100;
                                  } else {
                                    newRefunds[index].refund_percentage = value;
                                  }

                                  field.onChange(newRefunds);
                                }}
                                placeholder="0-100"
                              />
                              <span className="text-sm text-gray-500 min-w-[20px]">
                                %
                              </span>
                            </div>
                            {errors.refund_policies?.[index]
                              ?.refund_percentage && (
                              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                <AlertCircle className="h-3 w-3" />
                                {
                                  errors.refund_policies?.[index]
                                    ?.refund_percentage?.message
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}

                    <div className="flex items-center justify-between pt-4 border-t border-red-200">
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
                          onClick={() => {
                            if ((field.value?.length || 0) < 7) {
                              field.onChange([
                                ...(field.value || []),
                                {
                                  cancellation_period: "",
                                  refund_percentage: 0,
                                },
                              ]);
                            } else {
                              toast({
                                variant: "destructive",
                                description:
                                  "Maximum 7 refund criteria are allowed.",
                              });
                            }
                          }}
                          disabled={(field.value?.length || 0) >= 7}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Refund Rule
                        </Button>

                        {(field.value?.length || 0) > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-gray-500 hover:text-red-500"
                            onClick={() => {
                              const newRefunds = [...(field.value || [])];
                              newRefunds.pop();
                              field.onChange(newRefunds);
                            }}
                          >
                            <Minus className="h-4 w-4 mr-2" />
                            Remove Last
                          </Button>
                        )}
                      </div>

                      <div className="text-xs text-gray-500">
                        {field.value?.length || 0}/7 rules
                      </div>
                    </div>

                    {(field.value?.length || 0) >= 7 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="flex items-center gap-2 text-amber-700 text-sm">
                          <Info className="h-4 w-4" />
                          <span>
                            You've reached the maximum of 7 refund rules.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventPricingAndRefund;
